/**
 * Firestore-backed presentation management for Lectura Next.
 * Mirrors the legacy Lectura presenter settings flow:
 * - presentation doc keeps presenters + privacy
 * - presenterRequests/{uid} stores request/approval state
 * - userDirectory is searched for presenter suggestions
 */

import type { LecturaUser } from '../core/types';
import { ensureFirestoreDb, type FirestoreServiceOptions } from './firestore-service';

interface FirestoreDocSnap {
  exists: boolean;
  id?: string;
  data(): Record<string, unknown>;
}

interface FirestoreQuerySnap {
  docs: FirestoreDocSnap[];
}

interface FirestoreDocRef {
  collection(path: string): FirestoreCollectionRef;
  get(): Promise<FirestoreDocSnap>;
  set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
  onSnapshot?(cb: (snap: FirestoreDocSnap) => void, errCb?: (err: unknown) => void): () => void;
}

interface FirestoreCollectionRef {
  doc(id?: string): FirestoreDocRef;
  get(): Promise<FirestoreQuerySnap>;
  onSnapshot(cb: (snap: FirestoreQuerySnap) => void, errCb?: (err: unknown) => void): () => void;
  where(field: string, op: string, value: unknown): FirestoreCollectionRef;
  orderBy(field: string, direction?: 'asc' | 'desc'): FirestoreCollectionRef;
  startAt(value: string): FirestoreCollectionRef;
  endAt(value: string): FirestoreCollectionRef;
  limit(count: number): FirestoreCollectionRef;
}

interface FirestoreDb {
  collection(path: string): FirestoreCollectionRef;
  batch(): {
    set(ref: FirestoreDocRef, data: Record<string, unknown>, opts?: { merge?: boolean }): void;
    commit(): Promise<void>;
  };
}

export interface PresentationDocState {
  id: string;
  isPrivate?: boolean;
  presenters: string[];
}

export interface PresentationRequestState {
  id: string;
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  status: 'pending' | 'approved' | 'denied' | string;
  createdAt?: number;
  updatedAt?: number;
  reviewedAt?: number;
  reviewedBy?: string;
}

export interface PresentationDirectoryEntry {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
}

/** Keyed by normalized email from userDirectory. */
export type UserDirectoryProfileMap = Record<string, { photoURL?: string; displayName?: string }>;

export interface PresentationManagementService {
  available: boolean;
  getPresentationDoc(presId: string): Promise<PresentationDocState | null>;
  subscribePresentationDoc(
    presId: string,
    cb: (doc: PresentationDocState | null) => void,
    onError?: (err: Error) => void
  ): () => void;
  updatePresentationPrefs(
    presId: string,
    updates: Partial<Pick<PresentationDocState, 'isPrivate' | 'presenters'>>
  ): Promise<void>;
  getPresenterRequests(presId: string): Promise<PresentationRequestState[]>;
  subscribePresenterRequests(
    presId: string,
    cb: (requests: PresentationRequestState[]) => void,
    onError?: (err: Error) => void
  ): () => void;
  getMyPresenterRequest(presId: string): Promise<PresentationRequestState | null>;
  subscribeMyPresenterRequest(
    presId: string,
    cb: (request: PresentationRequestState | null) => void,
    onError?: (err: Error) => void
  ): () => void;
  submitPresenterRequest(presId: string): Promise<void>;
  reviewPresenterRequest(presId: string, requestUserId: string, approve: boolean): Promise<void>;
  searchUserDirectory(query: string, maxResults?: number): Promise<PresentationDirectoryEntry[]>;
  /** Emails of globally active admins (for presenter list). */
  listActiveAdminEmails(): Promise<string[]>;
  subscribeActiveAdminEmails(cb: (emails: string[]) => void, onError?: (err: Error) => void): () => void;
  lookupUserProfilesByEmails(emails: string[]): Promise<UserDirectoryProfileMap>;
}

interface CreatePresentationManagementOptions extends FirestoreServiceOptions {
  getCurrentUser: () => LecturaUser | null;
}

function normalizeEmail(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isEmailLike(value: unknown): boolean {
  return /.+@.+\..+/.test(normalizeEmail(value));
}

function presFirestoreDebug(...args: unknown[]): void {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[lectura-next][presentation-firestore]', ...args);
  }
}

function uniqEmails(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  values.forEach((value) => {
    const email = normalizeEmail(value);
    if (!isEmailLike(email) || seen.has(email)) return;
    seen.add(email);
    out.push(email);
  });
  return out;
}

function toMillis(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object') {
    const maybe = value as { seconds?: unknown; nanoseconds?: unknown; toMillis?: () => number };
    if (typeof maybe.toMillis === 'function') {
      const ms = maybe.toMillis();
      return Number.isFinite(ms) ? ms : undefined;
    }
    if (Number.isFinite(Number(maybe.seconds))) {
      const sec = Number(maybe.seconds);
      const nano = Number(maybe.nanoseconds || 0);
      return sec * 1000 + Math.floor(nano / 1e6);
    }
  }
  return undefined;
}

function mapPresentationDoc(doc: FirestoreDocSnap, raw: Record<string, unknown> | null): PresentationDocState {
  return {
    id: doc.id || '',
    isPrivate: raw?.isPrivate === true,
    presenters: uniqEmails([
      ...(Array.isArray(raw?.presenters) ? (raw?.presenters as unknown[]) : []),
      ...(Array.isArray(raw?.authors) ? (raw?.authors as unknown[]) : [])
    ])
  };
}

function mapRequestDoc(doc: FirestoreDocSnap): PresentationRequestState {
  const raw = doc.data() || {};
  return {
    id: doc.id || '',
    uid: String(raw.uid || doc.id || '').trim(),
    email: normalizeEmail(raw.email),
    displayName: String(raw.displayName || '').trim() || undefined,
    photoURL: String(raw.photoURL || '').trim() || undefined,
    status: String(raw.status || 'pending'),
    createdAt: toMillis(raw.createdAt),
    updatedAt: toMillis(raw.updatedAt),
    reviewedAt: toMillis(raw.reviewedAt),
    reviewedBy: String(raw.reviewedBy || '').trim() || undefined
  };
}

function getDbRef(db: FirestoreDb, presId: string): FirestoreDocRef {
  return db.collection('presentations').doc(String(presId));
}

function getPresenterRequestsRef(db: FirestoreDb, presId: string): FirestoreCollectionRef {
  return getDbRef(db, presId).collection('presenterRequests');
}

function getUserDirectoryRef(db: FirestoreDb): FirestoreCollectionRef {
  return db.collection('userDirectory');
}

export function createFirestorePresentationManagementService(
  opts: CreatePresentationManagementOptions
): PresentationManagementService {
  function toErr(err: unknown): Error {
    return err instanceof Error ? err : new Error(String(err));
  }

  function mapPendingRequests(snap: FirestoreQuerySnap): PresentationRequestState[] {
    return (snap.docs || []).map((doc) => mapRequestDoc(doc)).filter((req) => req.status === 'pending');
  }

  return {
    available: true,

    async getPresentationDoc(presId: string): Promise<PresentationDocState | null> {
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const ref = getDbRef(db, presId);
      const snap = await ref.get().catch(() => null);
      if (!snap || !snap.exists) return null;
      return mapPresentationDoc(snap, snap.data() || null);
    },

    subscribePresentationDoc(presId, cb, onError) {
      let closed = false;
      let unsub: (() => void) | null = null;
      ensureFirestoreDb(opts)
        .then((db) => {
          if (closed) return;
          const ref = getDbRef(db as unknown as FirestoreDb, presId);
          if (typeof ref.onSnapshot !== 'function') {
            // Fallback for environments without doc listeners.
            void ref
              .get()
              .then((snap) => cb(snap && snap.exists ? mapPresentationDoc(snap, snap.data() || null) : null))
              .catch((err) => onError?.(toErr(err)));
            return;
          }
          unsub = ref.onSnapshot(
            (snap) => cb(snap && snap.exists ? mapPresentationDoc(snap, snap.data() || null) : null),
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                cb(null);
                return;
              }
              onError?.(toErr(err));
            }
          );
        })
        .catch((err) => onError?.(toErr(err)));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    async updatePresentationPrefs(
      presId: string,
      updates: Partial<Pick<PresentationDocState, 'isPrivate' | 'presenters'>>
    ): Promise<void> {
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const ref = getDbRef(db, presId);
      const payload: Record<string, unknown> = { updatedAt: Date.now() };
      if (updates.isPrivate !== undefined) payload.isPrivate = updates.isPrivate === true;
      if (updates.presenters !== undefined) {
        const presenters = uniqEmails(updates.presenters);
        payload.presenters = presenters;
        payload.authors = presenters;
        const presSnap = await ref.get().catch(() => null);
        const oldPresenters =
          presSnap && presSnap.exists
            ? mapPresentationDoc(presSnap, presSnap.data() || null).presenters
            : [];
        const oldSet = new Set(oldPresenters.map((e) => normalizeEmail(e)));
        const addedEmails = new Set(
          presenters.filter((e) => isEmailLike(e) && !oldSet.has(normalizeEmail(e))).map((e) => normalizeEmail(e))
        );
        if (addedEmails.size > 0) {
          const manager = opts.getCurrentUser();
          const reviewedBy = manager && !manager.isAnonymous && isEmailLike(manager.email) ? normalizeEmail(manager.email) : '';
          const reviewedByUid = manager && !manager.isAnonymous ? String(manager.uid || '').trim() : '';
          const requestsSnap = await getPresenterRequestsRef(db, presId).get().catch(() => null);
          const batch = db.batch();
          batch.set(ref, payload, { merge: true });
          const now = Date.now();
          for (const doc of requestsSnap?.docs || []) {
            const raw = doc.data() || {};
            const em = normalizeEmail(raw.email);
            const st = String(raw.status || 'pending');
            if (st !== 'pending' || !addedEmails.has(em)) continue;
            const requestRef = getPresenterRequestsRef(db, presId).doc(String(doc.id || raw.uid || ''));
            batch.set(
              requestRef,
              {
                status: 'approved',
                reviewedAt: now,
                reviewedBy: reviewedBy || null,
                reviewedByUid: reviewedByUid || null,
                updatedAt: now
              },
              { merge: true }
            );
          }
          await batch.commit();
          return;
        }
      }
      await ref.set(payload, { merge: true });
    },

    async getPresenterRequests(presId: string): Promise<PresentationRequestState[]> {
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const col = getPresenterRequestsRef(db, presId);
      const snap = await col.orderBy('createdAt', 'desc').get().catch(async () => col.get());
      return mapPendingRequests(snap);
    },

    subscribePresenterRequests(presId, cb, onError) {
      let closed = false;
      let unsub: (() => void) | null = null;
      ensureFirestoreDb(opts)
        .then((db) => {
          if (closed) return;
          const col = getPresenterRequestsRef(db as unknown as FirestoreDb, presId);
          const ordered = col.orderBy('createdAt', 'desc');
          unsub = ordered.onSnapshot(
            (snap) => cb(mapPendingRequests(snap)),
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                cb([]);
                return;
              }
              onError?.(toErr(err));
            }
          );
        })
        .catch((err) => onError?.(toErr(err)));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    async getMyPresenterRequest(presId: string): Promise<PresentationRequestState | null> {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous || !user.uid) return null;
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const snap = await getPresenterRequestsRef(db, presId).doc(String(user.uid)).get().catch(() => null);
      if (!snap || !snap.exists) return null;
      return mapRequestDoc(snap);
    },

    subscribeMyPresenterRequest(presId, cb, onError) {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous || !user.uid) {
        cb(null);
        return () => {};
      }
      let closed = false;
      let unsub: (() => void) | null = null;
      ensureFirestoreDb(opts)
        .then((db) => {
          if (closed) return;
          const ref = getPresenterRequestsRef(db as unknown as FirestoreDb, presId).doc(String(user.uid));
          if (typeof ref.onSnapshot !== 'function') {
            void ref
              .get()
              .then((snap) => cb(snap && snap.exists ? mapRequestDoc(snap) : null))
              .catch((err) => onError?.(toErr(err)));
            return;
          }
          unsub = ref.onSnapshot(
            (snap) => cb(snap && snap.exists ? mapRequestDoc(snap) : null),
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                cb(null);
                return;
              }
              onError?.(toErr(err));
            }
          );
        })
        .catch((err) => onError?.(toErr(err)));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    async submitPresenterRequest(presId: string): Promise<void> {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous || !user.uid || !isEmailLike(user.email)) {
        throw new Error('Presenter request unavailable');
      }
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const ref = getPresenterRequestsRef(db, presId).doc(String(user.uid));
      await ref.set(
        {
          uid: String(user.uid || '').trim(),
          email: normalizeEmail(user.email),
          displayName: String(user.displayName || '').trim() || null,
          photoURL: String(user.photoURL || '').trim() || null,
          status: 'pending',
          updatedAt: Date.now(),
          createdAt: Date.now()
        },
        { merge: true }
      );
    },

    async reviewPresenterRequest(presId: string, requestUserId: string, approve: boolean): Promise<void> {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous || !user.uid) throw new Error('Presenter request review unavailable');
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const presRef = getDbRef(db, presId);
      const requestRef = getPresenterRequestsRef(db, presId).doc(String(requestUserId));
      const requestSnap = await requestRef.get();
      if (!requestSnap.exists) throw new Error('Presenter request not found');
      const requestData = requestSnap.data() || {};
      const requestEmail = normalizeEmail(requestData.email);
      const batch = db.batch();
      batch.set(
        requestRef,
        {
          status: approve ? 'approved' : 'denied',
          reviewedAt: Date.now(),
          reviewedBy: normalizeEmail(user.email),
          reviewedByUid: String(user.uid || '').trim(),
          updatedAt: Date.now()
        },
        { merge: true }
      );
      if (approve && isEmailLike(requestEmail)) {
        const presSnap = await presRef.get().catch(() => null);
        const current = presSnap && presSnap.exists ? mapPresentationDoc(presSnap, presSnap.data() || null) : null;
        const presenters = uniqEmails([...(current?.presenters || []), requestEmail]);
        batch.set(
          presRef,
          {
            presenters,
            authors: presenters,
            updatedAt: Date.now()
          },
          { merge: true }
        );
      }
      await batch.commit();
    },

    async searchUserDirectory(query: string, maxResults = 6): Promise<PresentationDirectoryEntry[]> {
      const user = opts.getCurrentUser();
      const search = String(query || '').trim().toLowerCase();
      const limit = Math.max(1, Math.min(Number(maxResults) || 6, 10));
      if (!user || user.isAnonymous || !search) return [];
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const usersRef = getUserDirectoryRef(db);
      const perQuery = Math.min(15, Math.max(limit, limit * 3));
      const range = (field: string) =>
        usersRef.orderBy(field).startAt(search).endAt(search + '\uf8ff').limit(perQuery).get().catch(() => null);
      const [emailSnap, nameSnap, t0Snap, t1Snap, tLastSnap] = await Promise.all([
        range('emailLower'),
        range('displayNameLower'),
        range('dirNameToken0'),
        range('dirNameToken1'),
        range('dirNameTokenLast')
      ]);
      const byUid = new Map<string, PresentationDirectoryEntry>();
      [emailSnap, nameSnap, t0Snap, t1Snap, tLastSnap].forEach((snap) => {
        (snap?.docs || []).forEach((doc) => {
          const raw = doc.data() || {};
          const uid = String(raw.uid || doc.id || '').trim();
          const email = normalizeEmail(raw.email);
          if (!uid || !isEmailLike(email)) return;
          byUid.set(uid, {
            uid,
            email,
            displayName: String(raw.displayName || '').trim() || undefined,
            photoURL: String(raw.photoURL || '').trim() || undefined
          });
        });
      });
      return Array.from(byUid.values())
        .sort((a, b) => {
          const aName = String(a.displayName || a.email).toLowerCase();
          const bName = String(b.displayName || b.email).toLowerCase();
          return aName.localeCompare(bName);
        })
        .slice(0, limit);
    },

    async listActiveAdminEmails(): Promise<string[]> {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous) {
        presFirestoreDebug('listActiveAdminEmails: skip (no user or anonymous)', {
          hasUser: !!user,
          isAnonymous: !!user?.isAnonymous
        });
        return [];
      }
      presFirestoreDebug('listActiveAdminEmails: querying admins where active==true', {
        uid: user.uid,
        email: user.email
      });
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      let snap: FirestoreQuerySnap;
      try {
        snap = await db.collection('admins').where('active', '==', true).get();
      } catch (err) {
        presFirestoreDebug('listActiveAdminEmails: Firestore query FAILED (permission/index/network?)', err);
        return [];
      }
      const rawDocs = snap.docs || [];
      presFirestoreDebug('listActiveAdminEmails: query returned docs', {
        docCount: rawDocs.length,
        docIds: rawDocs.map((d) => d.id)
      });
      const emails: string[] = [];
      const skippedIds: string[] = [];
      for (const doc of rawDocs) {
        const id = String(doc.id || '').trim();
        if (isEmailLike(id)) emails.push(normalizeEmail(id));
        else skippedIds.push(id);
      }
      if (skippedIds.length) {
        presFirestoreDebug('listActiveAdminEmails: doc ids skipped (not email-shaped)', skippedIds);
      }
      const out = [...new Set(emails)].sort((a, b) => a.localeCompare(b));
      presFirestoreDebug('listActiveAdminEmails: result emails', out);
      return out;
    },

    subscribeActiveAdminEmails(cb, onError) {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous) {
        cb([]);
        return () => {};
      }
      let closed = false;
      let unsub: (() => void) | null = null;
      ensureFirestoreDb(opts)
        .then((db) => {
          if (closed) return;
          const query = (db as unknown as FirestoreDb).collection('admins').where('active', '==', true);
          unsub = query.onSnapshot(
            (snap) => {
              const emails: string[] = [];
              (snap.docs || []).forEach((doc) => {
                const id = String(doc.id || '').trim();
                if (isEmailLike(id)) emails.push(normalizeEmail(id));
              });
              cb([...new Set(emails)].sort((a, b) => a.localeCompare(b)));
            },
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                cb([]);
                return;
              }
              onError?.(toErr(err));
            }
          );
        })
        .catch((err) => onError?.(toErr(err)));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    async lookupUserProfilesByEmails(emails: string[]): Promise<UserDirectoryProfileMap> {
      const user = opts.getCurrentUser();
      if (!user || user.isAnonymous || !emails.length) return {};
      const unique = uniqEmails(emails);
      if (!unique.length) return {};
      const db = (await ensureFirestoreDb(opts)) as unknown as FirestoreDb;
      const usersRef = getUserDirectoryRef(db);
      const result: UserDirectoryProfileMap = {};
      await Promise.all(
        unique.map(async (email) => {
          try {
            const snap = await usersRef.where('emailLower', '==', email).limit(1).get();
            const doc = snap.docs?.[0];
            if (!doc) return;
            const raw = doc.data() || {};
            const photoURL = String(raw.photoURL || '').trim() || undefined;
            const displayName = String(raw.displayName || '').trim() || undefined;
            if (photoURL || displayName) result[email] = { photoURL, displayName };
          } catch (err) {
            presFirestoreDebug('lookupUserProfilesByEmails query failed', { email, err });
          }
        })
      );
      return result;
    }
  };
}

export function createStubPresentationManagementService(): PresentationManagementService {
  const unavailable = async (): Promise<never> => {
    throw new Error('Presentation management is not available in this environment.');
  };
  return {
    available: false,
    getPresentationDoc: async () => null,
    subscribePresentationDoc: (_presId, cb) => {
      cb(null);
      return () => {};
    },
    updatePresentationPrefs: unavailable,
    getPresenterRequests: async () => [],
    subscribePresenterRequests: (_presId, cb) => {
      cb([]);
      return () => {};
    },
    getMyPresenterRequest: async () => null,
    subscribeMyPresenterRequest: (_presId, cb) => {
      cb(null);
      return () => {};
    },
    submitPresenterRequest: unavailable,
    reviewPresenterRequest: unavailable,
    searchUserDirectory: async () => [],
    listActiveAdminEmails: async () => [],
    subscribeActiveAdminEmails: (cb) => {
      cb([]);
      return () => {};
    },
    lookupUserProfilesByEmails: async () => ({})
  };
}
